import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { MenuCaret } from './MenuCaret';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { GET_COWORK_MCP_CONNECTOR } from '../../graphql/queries';
import { copyToClipboard, useCopy } from '../../lib/copyToClipboard';
import { AGENT_ACTIVITY } from './AgentStatus';
import { showInkToast } from './InkToast';
import { askAgentListeners, openAskAgent, type AskAgentOptions, type Listener } from './askAgentBus';
import {
  AGENT_FIRST,
  agentPrompt,
  askWords,
  type AgentFirstContext,
  type AgentFirstKey,
} from './agentFirst';

/**
 * Agent-first New and Edit (designer's notes, 2026-10-25): the "Ask your
 * agent · Recommended" menu on create and edit buttons (AgentFirstButton) and
 * the one "Ask your agent" modal they open. The words and prompts live in
 * agentFirst.ts. Call openAskAgent from anywhere; AppShell renders
 * <AskAgentHost />.
 */

// Account → AI Agents: where an agent is connected.
export const AI_AGENTS = '/settings/ai-agents';

/** Whether an agent is connected to the site (null until known), and its name when it has one. */
export function useSiteAgent(): { connected: boolean | null; name: string | null } {
  const { accountId } = useAccount();
  const { data: activity } = useQuery<{ agentActivity: { connected: boolean } }>(AGENT_ACTIVITY, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  const connected = activity ? activity.agentActivity.connected : null;
  const { data: connector } = useQuery<{ coworkMcpConnector: { label?: string | null } | null }>(
    GET_COWORK_MCP_CONNECTOR,
    { variables: { accountId }, skip: !accountId || !connected }
  );
  return { connected, name: (connected && connector?.coworkMcpConnector?.label?.trim()) || null };
}

// ── The modal ────────────────────────────────────────────────────────────────

export { openAskAgent, type AskAgentOptions };

/** "[topic]" in the prompt: the first one is selected, ready to type over. */
function selectFirstBracket(el: HTMLTextAreaElement) {
  const at = el.value.indexOf('[');
  const end = at >= 0 ? el.value.indexOf(']', at) : -1;
  if (at >= 0 && end > at) el.setSelectionRange(at, end + 1);
}

export function AskAgentHost() {
  const [ask, setAsk] = useState<AskAgentOptions | null>(null);
  const [text, setText] = useState('');
  const [failed, setFailed] = useState(false);
  const { connected, name } = useSiteAgent();
  const returnFocus = useRef<HTMLElement | null>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const titleId = useId();

  useEffect(() => {
    const fn: Listener = (next) => {
      returnFocus.current = document.activeElement as HTMLElement | null;
      setAsk(next);
      setText(agentPrompt(next.action, next.ctx));
      setFailed(false);
    };
    askAgentListeners.add(fn);
    return () => {
      askAgentListeners.delete(fn);
    };
  }, []);

  const close = useCallback(() => {
    setAsk(null);
    returnFocus.current?.focus?.();
  }, []);

  useEffect(() => {
    if (!ask) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener('keydown', onKey, true);
    const el = field.current;
    if (el) {
      el.focus();
      selectFirstBracket(el);
    }
    return () => document.removeEventListener('keydown', onKey, true);
  }, [ask, close]);

  if (!ask) return null;
  const self = AGENT_FIRST[ask.action].self;
  const words = askWords(name);
  const onSelf = ask.onSelf;

  return (
    <div
      className="ink-modal-back af-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="ink-modal af-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="af-head">
          <div className="af-title" id={titleId}>
            {words.title}
          </div>
          <p className="af-sub">{words.sub}</p>
        </div>
        <div className="af-body">
          <label className="af-field">
            Prompt
            <textarea
              ref={field}
              className="ink-input af-prompt"
              rows={5}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </label>
          <span className="af-help">
            Fill in the parts in [brackets], or leave them for your agent to ask about.
          </span>
        </div>
        <div className="af-agent">
          <span
            className="af-dot"
            style={{ background: connected ? 'var(--ink-green)' : 'var(--ink-amber)' }}
          />
          <span className="af-agent-line">
            {connected
              ? `${name || 'Your agent'} is connected to this site`
              : 'No agent is connected to this site yet'}
          </span>
          <Link to={AI_AGENTS} className="af-agent-link" onClick={() => setAsk(null)}>
            {connected ? 'AI Agents' : 'Connect an agent'}
          </Link>
        </div>
        <div className="af-foot">
          {self && onSelf ? (
            <button
              type="button"
              className="af-self"
              onClick={() => {
                setAsk(null);
                onSelf();
              }}
            >
              {self.label} instead
            </button>
          ) : null}
          {failed ? (
            <span className="af-failed" role="alert">
              Couldn’t copy. Select the prompt and copy it.
            </span>
          ) : null}
          <button type="button" className="ink-btn" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="ink-btn ink-btn-primary"
            onClick={async (e) => {
              if (await copyToClipboard(text, e.currentTarget)) {
                close();
                showInkToast(words.copied);
              } else setFailed(true);
            }}
          >
            Copy prompt
          </button>
        </div>
      </div>
    </div>
  );
}

// ── The button and its menu ──────────────────────────────────────────────────

export function AgentFirstButton({
  action,
  label,
  ctx,
  onSelf,
  className = 'ink-btn ink-btn-primary',
  wrapClassName,
  align = 'right',
  footer,
  disabled,
  title,
  onAsk,
}: {
  action: AgentFirstKey;
  label: React.ReactNode;
  ctx?: AgentFirstContext;
  /** The do-it-myself action; required when the action has a form. */
  onSelf?: () => void;
  /** Runs instead of the copy-a-prompt modal (an article's own Ask agent box). */
  onAsk?: () => void;
  className?: string;
  /** On the span that holds the button and its menu, for where it sits in the layout. */
  wrapClassName?: string;
  /** Which edge of the button the menu lines up with. */
  align?: 'left' | 'right' | 'center';
  /** Under the two items (New campaign's "Sharing every new article? …"). */
  footer?: (close: () => void) => React.ReactNode;
  disabled?: boolean;
  title?: string;
}) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const cfg = AGENT_FIRST[action];
  const self = cfg.self && onSelf ? cfg.self : null;
  const ask = onAsk || (() => openAskAgent({ action, ctx, onSelf: self ? onSelf : undefined }));

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // No form to fill in: the button asks the agent straight away.
  if (!self) {
    return (
      <button
        type="button"
        className={className}
        aria-haspopup="dialog"
        disabled={disabled}
        title={title}
        onClick={ask}
      >
        {label}
      </button>
    );
  }

  // Choosing closes the menu first, then runs the action.
  const choose = (run: () => void) => () => {
    setOpen(false);
    run();
  };
  return (
    <span className={wrapClassName ? `af-menu-wrap ${wrapClassName}` : 'af-menu-wrap'}>
      <button
        type="button"
        className={className}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={disabled}
        title={title}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <MenuCaret className="af-caret" />
      </button>
      {open ? (
        <>
          <span className="af-scrim" onClick={() => setOpen(false)} />
          <span className="af-menu" role="menu" id={menuId} data-align={align}>
            <button type="button" role="menuitem" autoFocus onClick={choose(ask)}>
              <span className="af-item-label">
                Ask your agent<span className="af-tag">Recommended</span>
              </span>
              <span className="af-item-sub">{cfg.agentSub}</span>
            </button>
            <button type="button" role="menuitem" onClick={choose(onSelf as () => void)}>
              <span className="af-item-label">{self.label}</span>
              <span className="af-item-sub">{self.sub}</span>
            </button>
            {footer ? footer(() => setOpen(false)) : null}
          </span>
        </>
      ) : null}
    </span>
  );
}

// ── Inline prompt (Design → Checks) ──────────────────────────────────────────

/**
 * A prompt to copy, shown in place (Content → Design → Checks): a line saying
 * what comes back, the prompt, the way to the agent (its name when one is
 * connected, "Connect an agent" when none is) and Copy prompt.
 */
export function AgentPromptBody({
  prompt,
  note,
  onCopied,
}: {
  prompt: string;
  note: string;
  onCopied?: () => void;
}) {
  const { copy, state } = useCopy();
  const { connected, name } = useSiteAgent();
  const copied = state('prompt');
  return (
    <>
      <p>{note}</p>
      <div className="pg-agent-prompt">{prompt}</div>
      <div className="pg-agent-panel-foot">
        {connected === false ? (
          <Link to={AI_AGENTS}>Connect an agent</Link>
        ) : (
          <Link to={AI_AGENTS}>{name ? `Your agent: ${name} →` : 'Your agent →'}</Link>
        )}
        <button
          type="button"
          className="ink-btn ink-btn-primary"
          onClick={async (e) => {
            if (await copy('prompt', prompt, e)) onCopied?.();
          }}
        >
          {copied === 'copied' ? 'Copied' : copied === 'failed' ? 'Couldn’t copy' : 'Copy prompt'}
        </button>
      </div>
    </>
  );
}
